import { CommandLoading } from 'cmdk';
import { type Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib';
import { Button } from '../../ui/button';
import { Command, CommandInput, CommandItem, CommandList, cmdkItemValue } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Spinner } from '../../ui/spinner';
import { Center } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type SearchInputProps<T extends object> = {
  search: string;
  setSearch: (search: string) => void;
  selected: T | null;
  isLoading: boolean;
  searchPlaceholder?: string;
  placeholder: string;
  items: T[];
  itemKey: keyof T;
  renderItem: (item: T) => React.ReactNode;
  renderSelected?: (item: T) => React.ReactNode;
  onSelect: (item: T) => void;
  ref?: Ref<HTMLInputElement>;
  disabled?: boolean;
  className?: string;
  errorMessage?: string;
};

export const SearchInput = <T extends object>({
  search,
  setSearch,
  isLoading,
  searchPlaceholder,
  placeholder,
  selected,
  items,
  renderItem,
  renderSelected = renderItem,
  onSelect,
  itemKey,
  ref,
  disabled,
  className,
  errorMessage,
}: SearchInputProps<T>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const shouldShowPlaceholder = !selected;

  return (
    // modal: the portalled list sits outside a parent Dialog's scroll-lock; modal restores wheel scroll.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            shouldShowPlaceholder && 'text-muted-foreground hover:text-gray-600',
            disabled && 'bg-gray-50 opacity-50',
            className,
          )}
        >
          {shouldShowPlaceholder ? placeholder : renderSelected(selected)}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        alignOffset={0}
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        avoidCollisions
      >
        <Command onValueChange={() => setOpen(false)} shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder ?? t('searchInput.searchPlaceholder')}
            value={search}
            onValueChange={setSearch}
            ref={ref}
          />
          {isLoading && (
            <CommandLoading>
              <Center className="p-3">
                <Spinner />
              </Center>
            </CommandLoading>
          )}
          {errorMessage && (
            <Text size="sm" tone="destructive" className="p-3">
              {errorMessage}
            </Text>
          )}
          <CommandList>
            {items.map(item => (
              <CommandItem
                value={cmdkItemValue(item[itemKey] as string)}
                key={item[itemKey] as string}
                onSelect={() => {
                  onSelect(item);
                  setOpen(false);
                }}
              >
                {renderItem(item)}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
